import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, Users, Star, CalendarClock, CheckCircle2, Lightbulb, ArrowRight, Phone } from "lucide-react";
import { getStats, getTips, getLeads } from "@/lib/apiClient";
import { CallReport } from "@/components/CallReport";
import { SourceReport } from "@/components/SourceReport";
import { CallQueue } from "@/components/CallQueue";
import { useFeedbackUnread } from "@/components/FeedbackBadge";
import { MessageCircleQuestion } from "lucide-react";

function FeedbackNotice() {
  const count = useFeedbackUnread();
  const navigate = useNavigate();
  if (!count) return null;
  return (
    <button data-testid="dashboard-feedback-notice" onClick={() => navigate("/vragen")} className="w-full mb-8 flex items-center gap-3 rounded-2xl border border-[#16BFBF] bg-[#EAFBFB] px-5 py-4 text-left hover:bg-[#D6F3F3] transition-colors">
      <MessageCircleQuestion className="h-5 w-5 text-[#0C6E6E]" />
      <span className="font-medium text-[#001C51]">{count} nieuwe {count === 1 ? "vraag of suggestie" : "vragen of suggesties"} van medewerkers</span>
      <span className="ml-auto text-sm text-[#0C6E6E] font-medium">Bekijken →</span>
    </button>
  );
}
import { useAuth } from "@/context/AuthContext";

const CAT_LABELS = { verkoper: "Verkoper", verhuurder: "Verhuurder", koper: "Koper", huurder: "Huurder", partner: "Partner", overig: "Overig" };
const todayStr = () => new Date().toISOString().slice(0, 10);

function Metric({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#E2E7EC] bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
          <Icon className="h-5 w-5 text-[#001C51]" strokeWidth={1.75} />
        </div>
        <p className="text-sm text-[#5B6675]">{label}</p>
      </div>
      <p className="font-heading font-bold text-4xl text-[#16202B]">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState([]);
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    getTips().then(setTips).catch(() => {});
    getLeads().then(setLeads).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Goedemorgen" : hour < 18 ? "Goedemiddag" : "Goedenavond";

  const dueList = leads
    .filter((l) => l.follow_up_date && l.follow_up_date.slice(0, 10) <= todayStr() && !["appointment_scheduled", "assignment_received", "order_placed", "closed", "not_interested"].includes(l.pipeline_status) && !["not_interested", "invalid_number"].includes(l.call_outcome))
    .sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date));

  return (
    <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto fe-fade-up">
      <header className="mb-10">
        <p className="text-[#16BFBF] font-medium mb-1">{greeting}, klaar om te bellen?</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#16202B]">Overzicht</h1>
        <p className="text-[#5B6675] mt-3 max-w-xl">
          Jouw rustige startpunt vóór elk gesprek. Adem in en bekijk wie je vandaag moet bellen.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric icon={CalendarClock} label="Bellen vandaag" value={dueList.length} tint="#D6F3F3" testid="stat-due" />
        <Metric icon={Users} label="Leads totaal" value={stats?.total ?? "–"} tint="#EDF1F5" testid="stat-total" />
        <Metric icon={Star} label="Nog te bellen" value={stats?.awaiting_contact ?? "–"} tint="#E3E9F5" testid="stat-awaiting" />
        <Metric icon={PhoneCall} label="Belpogingen" value={stats?.calls_made ?? "–"} tint="#D6F3F3" testid="stat-calls" />
      </div>

      {user?.role === "admin" && <FeedbackNotice />}
      <CallQueue />
      {user && <CallReport />}
      {["admin", "teamleider"].includes(user?.role) && <SourceReport />}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <button
          data-testid="cta-start-script"
          onClick={() => navigate("/belscript")}
          className="lg:col-span-2 group text-left rounded-2xl bg-[#001C51] text-white p-8 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
        >
          <PhoneCall className="h-8 w-8 mb-4 opacity-90" strokeWidth={1.5} />
          <h2 className="font-heading font-semibold text-2xl mb-2">Start een gesprek</h2>
          <p className="text-white/80 max-w-md">Open het stap-voor-stap belscript met grote, leesbare tekst en een klikbare beslisboom naast je.</p>
          <span className="inline-flex items-center gap-2 mt-5 font-medium">
            Naar belscript <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>

        <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-[#16BFBF]" strokeWidth={1.75} />
            <p className="font-heading font-semibold text-[#16202B]">Snelle tips</p>
          </div>
          <ul className="space-y-3">
            {tips.slice(0, 4).map((t, i) => (
              <li key={i} className="text-sm text-[#5B6675] leading-relaxed flex gap-2">
                <span className="text-[#001C51] mt-0.5">›</span> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
